import { ApplicationTypes, Applications, Phases, Sectors, Shifts, Tasks } from '../../services';
import {
    buildRequestFilters,
    buildRequestRange,
    scopeApplicationTypes,
} from '../../containers/Dashboard/Widgets/Volunteers/dashboardVolunteersParams';
import { DASHBOARD_VOLUNTEERS_STALE_MS } from './dashboardVolunteersConstants';
import {
    APPLICATION_LISTING_FIELDS,
    APPLICATION_TYPE_LISTING_FIELDS,
    PHASE_LISTING_FIELDS,
    SECTOR_SCOPE_LISTING_FIELDS,
    SHIFT_LISTING_FIELDS,
    TASK_LISTING_FIELDS,
} from './dashboardVolunteersConstants';

export { DASHBOARD_VOLUNTEERS_STALE_MS };

const LOAD_DASHBOARD_VOLUNTEERS_REQUEST = 'kronos/dashboardVolunteers/LOAD_REQUEST';
const LOAD_DASHBOARD_VOLUNTEERS_SUCCESS = 'kronos/dashboardVolunteers/LOAD_SUCCESS';
const LOAD_DASHBOARD_VOLUNTEERS_FAILURE = 'kronos/dashboardVolunteers/LOAD_FAILURE';

const safeTasks = (tasks) => Array.isArray(tasks) ? tasks : [];
const safeApps = (applications) => Array.isArray(applications) ? applications : [];
const safeShifts = (shifts) => Array.isArray(shifts) ? shifts : [];
const safeSectors = (sectors) => Array.isArray(sectors) ? sectors : [];
const safeApplicationTypes = (applicationTypes) => Array.isArray(applicationTypes) ? applicationTypes : [];

const emptyData = () => ({
    tasks: [],
    shifts: [],
    applications: [],
    applicationTypes: [],
    phases: [],
    sectors: [],
});

const initialState = {
    cacheKey: null,
    fetchedAt: null,
    lastRequestContext: null,
    ...emptyData(),
    isLoading: false,
    listFetchScope: 'idle',
    hasLoadedOnce: false,
    error: null,
};

/** In-flight loads keyed by cacheKey — avoids duplicate parallel fetches. */
const inFlightLoads = new Map();

const hasCachedData = (state) => (
    state.hasLoadedOnce
    || state.tasks.length > 0
    || state.shifts.length > 0
    || state.applications.length > 0
    || state.applicationTypes.length > 0
);

export default function dashboardVolunteersReducer(state = initialState, action) {
    switch (action.type) {
        case LOAD_DASHBOARD_VOLUNTEERS_REQUEST: {
            const cacheKeyChanged = Boolean(
                state.cacheKey && state.cacheKey !== action.cacheKey
            );
            const isMinorFetch = action.fetchScope === 'minor';
            const clearStaleRows = cacheKeyChanged && !isMinorFetch;
            return {
                ...state,
                ...(clearStaleRows ? emptyData() : {}),
                cacheKey: action.cacheKey,
                isLoading: true,
                listFetchScope: action.fetchScope,
                error: null,
                lastRequestContext: action.requestContext,
                hasLoadedOnce: clearStaleRows ? false : state.hasLoadedOnce,
            };
        }
        case LOAD_DASHBOARD_VOLUNTEERS_SUCCESS:
            if (action.cacheKey !== state.cacheKey)
                return state;
            return {
                ...state,
                ...action.data,
                cacheKey: action.cacheKey,
                fetchedAt: action.fetchedAt,
                isLoading: false,
                listFetchScope: 'idle',
                hasLoadedOnce: true,
                error: null,
            };
        case LOAD_DASHBOARD_VOLUNTEERS_FAILURE:
            if (action.cacheKey !== state.cacheKey)
                return state;
            return {
                ...state,
                isLoading: false,
                listFetchScope: 'idle',
                error: action.error,
            };
        default:
            return state;
    }
}

async function fetchMetadata({ permissions }) {
    const {
        canUseTasks,
        canUseApplications,
        canUseShifts,
    } = permissions;

    const data = {
        applications: [],
        applicationTypes: [],
        phases: [],
        sectors: [],
    };

    const requests = [];

    if (canUseApplications) {
        requests.push(
            Applications.list({ fields: APPLICATION_LISTING_FIELDS }).then((result) => {
                data.applications = safeApps(result?.applications);
            })
        );
    }

    if (canUseTasks || canUseShifts) {
        requests.push(
            Sectors.list({ fields: SECTOR_SCOPE_LISTING_FIELDS })
                .then((result) => {
                    data.sectors = safeSectors(result?.sectors);
                })
                .catch(() => {
                    data.sectors = [];
                })
        );
    }

    if (canUseShifts) {
        requests.push(
            Shifts.getLinkedResources(['phases'])
                .then((result) => {
                    data.phases = Array.isArray(result?.linked_resources?.phases)
                        ? result.linked_resources.phases
                        : [];
                })
                .catch(() => {
                    data.phases = [];
                })
        );
    } else if (canUseTasks) {
        requests.push(
            Phases.list({ fields: PHASE_LISTING_FIELDS })
                .then((result) => {
                    data.phases = Array.isArray(result?.phases) ? result.phases : [];
                })
                .catch(() => {
                    data.phases = [];
                })
        );
    }

    requests.push(
        ApplicationTypes.list({ fields: APPLICATION_TYPE_LISTING_FIELDS })
            .then((result) => {
                data.applicationTypes = safeApplicationTypes(result?.application_types);
            })
            .catch(() => {
                data.applicationTypes = [];
            })
    );

    await Promise.all(requests);
    return data;
}

async function fetchPeriodData({ permissions, requestFilters }) {
    const { canUseTasks, canUseShifts } = permissions;
    const data = { tasks: [], shifts: [] };

    if (!canUseTasks && !canUseShifts)
        return data;

    const requests = [];

    if (canUseTasks) {
        requests.push(
            Tasks.list({ ...requestFilters, fields: TASK_LISTING_FIELDS }).then((result) => {
                data.tasks = safeTasks(result?.tasks);
            })
        );
    }

    if (canUseShifts) {
        requests.push(
            Shifts.list({ ...requestFilters, fields: SHIFT_LISTING_FIELDS }).then((result) => {
                data.shifts = safeShifts(result?.shifts);
            })
        );
    }

    await Promise.all(requests);
    return data;
}

function buildRequestContext(options) {
    const {
        cacheKey,
        permissions,
        periodSelections,
        isAdvancedMode,
        hasEditions,
        currentEditionId,
    } = options;
    return {
        cacheKey,
        permissions,
        periodSelections,
        isAdvancedMode,
        hasEditions,
        currentEditionId,
    };
}

/**
 * @param {object} options
 * @param {string} options.cacheKey
 * @param {object} options.permissions
 * @param {object} options.periodSelections
 * @param {boolean} options.isAdvancedMode
 * @param {boolean} options.hasEditions
 * @param {string|null} options.currentEditionId
 * @param {boolean} [options.forceRefresh]
 * @param {'major'|'minor'} [options.fetchScope]
 */
export function loadDashboardVolunteers(options) {
    const {
        cacheKey,
        permissions,
        periodSelections,
        isAdvancedMode,
        hasEditions,
        currentEditionId,
        forceRefresh = false,
        fetchScope: requestedFetchScope,
    } = options;

    const requestContext = buildRequestContext(options);

    return (dispatch, getState) => {
        const state = getState().dashboardVolunteers || initialState;
        const sameCacheKey = state.cacheKey === cacheKey && hasCachedData(state);
        const ageMs = state.fetchedAt ? Date.now() - state.fetchedAt : Infinity;
        const isFresh = sameCacheKey && ageMs < DASHBOARD_VOLUNTEERS_STALE_MS;

        if (!forceRefresh && isFresh)
            return Promise.resolve();

        if (inFlightLoads.has(cacheKey))
            return inFlightLoads.get(cacheKey);

        const hadCachedData = hasCachedData(state);

        let fetchScope = requestedFetchScope;
        if (!forceRefresh && sameCacheKey && ageMs >= DASHBOARD_VOLUNTEERS_STALE_MS) {
            fetchScope = 'minor';
        } else if (!fetchScope) {
            // Soft-refresh only when reusing the same cache intent.
            // Period/scope changes alter cacheKey; keeping stale rows makes the
            // client filter (e.g. past range vs future tasks) show empty coverage.
            fetchScope = (sameCacheKey && hadCachedData) ? 'minor' : 'major';
        }

        const loadPromise = dispatch(runLoad({
            cacheKey,
            permissions,
            periodSelections,
            isAdvancedMode,
            hasEditions,
            currentEditionId,
            requestContext,
            fetchScope,
        }));

        inFlightLoads.set(cacheKey, loadPromise);
        loadPromise.finally(() => {
            if (inFlightLoads.get(cacheKey) === loadPromise)
                inFlightLoads.delete(cacheKey);
        });

        return loadPromise;
    };
}

export function forceRefreshDashboardVolunteers() {
    return (dispatch, getState) => {
        const ctx = getState().dashboardVolunteers?.lastRequestContext;
        if (!ctx)
            return Promise.resolve();
        return dispatch(loadDashboardVolunteers({
            ...ctx,
            forceRefresh: true,
            fetchScope: 'minor',
        }));
    };
}

function runLoad({
    cacheKey,
    permissions,
    periodSelections,
    isAdvancedMode,
    hasEditions,
    currentEditionId,
    requestContext,
    fetchScope,
}) {
    return async (dispatch) => {
        dispatch({
            type: LOAD_DASHBOARD_VOLUNTEERS_REQUEST,
            cacheKey,
            fetchScope,
            requestContext,
        });

        try {
            const metadata = await fetchMetadata({ permissions });
            const scopedApplicationTypes = scopeApplicationTypes(
                metadata.applicationTypes,
                hasEditions,
                currentEditionId
            );
            const requestRange = buildRequestRange({
                isAdvancedMode,
                scopedApplicationTypes,
                periodSelections,
                phases: metadata.phases,
            });
            const requestFilters = buildRequestFilters(requestRange);
            const periodData = await fetchPeriodData({ permissions, requestFilters });

            dispatch({
                type: LOAD_DASHBOARD_VOLUNTEERS_SUCCESS,
                cacheKey,
                fetchedAt: Date.now(),
                data: {
                    ...metadata,
                    ...periodData,
                },
            });
        } catch (error) {
            dispatch({
                type: LOAD_DASHBOARD_VOLUNTEERS_FAILURE,
                cacheKey,
                error: error?.message || 'Failed to load dashboard volunteers',
            });
        }
    };
}
